import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { createSector, getSector, updateSector, clearSector } from '../../actions/sector';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { ArrowLeft, Save, MapPin, Map, Activity, FileText, Building, Clock, Layers, Phone, User } from 'lucide-react';

const initialState = {
    name: '',
    description: '',
    serviceableArea: '',
    isActive: true,
    maxDriveTimeMinutes: 30,
    boundary: {
        type: 'Polygon',
        coordinates: [[]]
    },
    centerPoint: {
        type: 'Point',
        coordinates: [0, 0]
    },
    microWarehouse: {
        inventoryNode: '',
        location: {
            type: 'Point',
            coordinates: [0, 0]
        },
        address: '',
        contactPerson: '',
        contactNumber: ''
    }
};

const Sector = ({
    createSector,
    getSector,
    updateSector,
    clearSector,
    getServiceableAreas,
    sector,
    serviceableAreas,
    loading
}) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(initialState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [coordinates, setCoordinates] = useState('');
    const [centerCoords, setCenterCoords] = useState('');
    const [warehouseCoords, setWarehouseCoords] = useState('');

    const isNewSector = id === 'new';

    useEffect(() => {
        document.title = isNewSector ? 'Create Sector' : 'Edit Sector';
        
        // Load serviceable areas for dropdown
        getServiceableAreas();
        
        if (!isNewSector && (!sector || sector._id !== id)) {
            getSector(id);
        } else if (!isNewSector && sector && sector._id === id) {
            setFormData({ ...initialState, ...sector });
            
            // Convert coordinates to string format for editing
            if (sector.boundary?.coordinates?.[0]) {
                const coordsString = sector.boundary.coordinates[0]
                    .map(coord => `${coord[0]},${coord[1]}`)
                    .join(' ');
                setCoordinates(coordsString);
            }
            
            if (sector.centerPoint?.coordinates) {
                setCenterCoords(`${sector.centerPoint.coordinates[0]},${sector.centerPoint.coordinates[1]}`);
            }
            
            if (sector.microWarehouse?.location?.coordinates) {
                setWarehouseCoords(`${sector.microWarehouse.location.coordinates[0]},${sector.microWarehouse.location.coordinates[1]}`);
            }
        } else if (isNewSector) {
            clearSector();
            setFormData(initialState);
            setCoordinates('');
            setCenterCoords('');
            setWarehouseCoords('');
        }
    }, [id, isNewSector, sector, getSector, clearSector, getServiceableAreas]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }

        if (name.includes('microWarehouse.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                microWarehouse: {
                    ...prev.microWarehouse,
                    [field]: type === 'checkbox' ? checked : value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleCoordinatesChange = (e) => {
        const value = e.target.value;
        setCoordinates(value);
        
        if (errors.coordinates) {
            setErrors(prev => ({ ...prev, coordinates: '' }));
        }
    };

    const handleCenterCoordsChange = (e) => {
        const value = e.target.value;
        setCenterCoords(value);
        
        if (errors.centerCoords) {
            setErrors(prev => ({ ...prev, centerCoords: '' }));
        }
    };

    const handleWarehouseCoordsChange = (e) => {
        const value = e.target.value;
        setWarehouseCoords(value);
        
        if (errors.warehouseCoords) {
            setErrors(prev => ({ ...prev, warehouseCoords: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Sector name is required';
        }

        if (!formData.serviceableArea) {
            newErrors.serviceableArea = 'Serviceable area is required';
        }

        if (!formData.maxDriveTimeMinutes || formData.maxDriveTimeMinutes < 1) {
            newErrors.maxDriveTimeMinutes = 'Max drive time must be at least 1 minute';
        }

        // Validate boundary coordinates
        if (!coordinates.trim()) {
            newErrors.coordinates = 'Boundary coordinates are required';
        } else {
            const coordPairs = coordinates.trim().split(' ');
            if (coordPairs.length < 3) {
                newErrors.coordinates = 'At least 3 coordinate pairs are required for a polygon';
            } else {
                const validCoords = coordPairs.every(pair => {
                    const [lng, lat] = pair.split(',');
                    return !isNaN(parseFloat(lng)) && !isNaN(parseFloat(lat));
                });
                
                if (!validCoords) {
                    newErrors.coordinates = 'Invalid coordinate format. Use: lng,lat lng,lat lng,lat...';
                }
            }
        }

        // Validate center point
        if (!centerCoords.trim()) {
            newErrors.centerCoords = 'Center point coordinates are required';
        } else {
            const [lng, lat] = centerCoords.split(',');
            if (isNaN(parseFloat(lng)) || isNaN(parseFloat(lat))) {
                newErrors.centerCoords = 'Invalid center point format. Use: lng,lat';
            }
        }

        // Validate microwarehouse if provided
        if (formData.microWarehouse.address.trim()) {
            if (!warehouseCoords.trim()) {
                newErrors.warehouseCoords = 'Warehouse coordinates are required when address is provided';
            } else {
                const [lng, lat] = warehouseCoords.split(',');
                if (isNaN(parseFloat(lng)) || isNaN(parseFloat(lat))) {
                    newErrors.warehouseCoords = 'Invalid warehouse coordinates format. Use: lng,lat';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const parseCoordinates = () => {
        const coordPairs = coordinates.trim().split(' ');
        const parsedCoords = coordPairs.map(pair => {
            const [lng, lat] = pair.split(',');
            return [parseFloat(lng), parseFloat(lat)];
        });
        
        // Ensure the polygon is closed
        if (parsedCoords.length > 0) {
            const first = parsedCoords[0];
            const last = parsedCoords[parsedCoords.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
                parsedCoords.push([...first]);
            }
        }
        
        return [parsedCoords];
    };

    const parseCenterPoint = () => {
        const [lng, lat] = centerCoords.split(',');
        return [parseFloat(lng), parseFloat(lat)];
    };

    const parseWarehouseLocation = () => {
        if (!warehouseCoords.trim()) return [0, 0];
        const [lng, lat] = warehouseCoords.split(',');
        return [parseFloat(lng), parseFloat(lat)];
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        try {
            setIsSubmitting(true);
            
            const submissionData = {
                ...formData,
                boundary: {
                    type: 'Polygon',
                    coordinates: parseCoordinates()
                },
                centerPoint: {
                    type: 'Point',
                    coordinates: parseCenterPoint()
                },
                microWarehouse: {
                    ...formData.microWarehouse,
                    location: {
                        type: 'Point',
                        coordinates: parseWarehouseLocation()
                    }
                }
            };
            
            if (isNewSector) {
                await createSector(submissionData);
                navigate('/inventory/v1.2/sectors');
            } else {
                await updateSector(sector._id, submissionData);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && !isNewSector) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading sector details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <Link
                                className="btn btn-outline-primary d-flex align-items-center"
                                to="/inventory/v1.2/sectors"
                            >
                                <ArrowLeft size={16} className="me-2" />
                                Back to Sectors
                            </Link>
                            <h2 className="text-primary mb-0 fw-bold">
                                {isNewSector ? 'Create Sector' : `Edit ${formData.name}`}
                            </h2>
                            <div></div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit}>
                    <div className="row">
                        {/* Main Form */}
                        <div className="col-lg-8">
                            {/* Basic Information */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <FileText size={20} className="me-2" />
                                        Basic Information
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Sector Name *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                placeholder="Enter sector name"
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Serviceable Area *</label>
                                            <select
                                                name="serviceableArea"
                                                value={formData.serviceableArea._id || ''}
                                                onChange={handleChange}
                                                className={`form-select ${errors.serviceableArea ? 'is-invalid' : ''}`}
                                            >
                                                <option value="">Select serviceable area</option>
                                                {serviceableAreas?.map(area => (
                                                    <option key={area._id} value={area._id}>
                                                        {area.name} ({area.city}, {area.state})
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.serviceableArea && <div className="invalid-feedback">{errors.serviceableArea}</div>}
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-bold">Description</label>
                                            <textarea
                                                name="description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter sector description (optional)"
                                                rows="3"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Max Drive Time (minutes) *</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Clock size={16} />
                                                </span>
                                                <input
                                                    type="number"
                                                    name="maxDriveTimeMinutes"
                                                    value={formData.maxDriveTimeMinutes}
                                                    onChange={handleChange}
                                                    className={`form-control ${errors.maxDriveTimeMinutes ? 'is-invalid' : ''}`}
                                                    min="1"
                                                    max="240"
                                                />
                                                {errors.maxDriveTimeMinutes && <div className="invalid-feedback">{errors.maxDriveTimeMinutes}</div>}
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Status</label>
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleChange}
                                                    className="form-check-input"
                                                    id="isActive"
                                                />
                                                <label className="form-check-label" htmlFor="isActive">
                                                    <Activity size={16} className="me-1" />
                                                    {formData.isActive ? 'Active' : 'Inactive'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Geographic Data */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-info text-white">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Map size={20} className="me-2" />
                                        Geographic Data
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="alert alert-info">
                                        <strong>Coordinate Format:</strong> Enter coordinates as longitude,latitude pairs.
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Sector Boundary Coordinates *</label>
                                        <textarea
                                            value={coordinates}
                                            onChange={handleCoordinatesChange}
                                            className={`form-control ${errors.coordinates ? 'is-invalid' : ''}`}
                                            placeholder="Enter boundary coordinates as: lng,lat lng,lat lng,lat..."
                                            rows="3"
                                            style={{ fontFamily: 'monospace' }}
                                        />
                                        {errors.coordinates && <div className="invalid-feedback">{errors.coordinates}</div>}
                                        <div className="form-text">
                                            Minimum 3 coordinate pairs required for a polygon boundary.
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Center Point Coordinates *</label>
                                        <input
                                            type="text"
                                            value={centerCoords}
                                            onChange={handleCenterCoordsChange}
                                            className={`form-control ${errors.centerCoords ? 'is-invalid' : ''}`}
                                            placeholder="lng,lat (e.g., 77.5946,12.9716)"
                                            style={{ fontFamily: 'monospace' }}
                                        />
                                        {errors.centerCoords && <div className="invalid-feedback">{errors.centerCoords}</div>}
                                        <div className="form-text">
                                            The central point of the sector for calculations.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Micro Warehouse */}
                            <div className="card shadow-sm mb-4">
                                <div className="card-header bg-warning text-dark">
                                    <h5 className="mb-0 d-flex align-items-center">
                                        <Building size={20} className="me-2" />
                                        Micro Warehouse
                                    </h5>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Inventory Node ID</label>
                                            <input
                                                type="text"
                                                name="microWarehouse.inventoryNode"
                                                value={formData.microWarehouse.inventoryNode}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter inventory node ID"
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Contact Person</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <User size={16} />
                                                </span>
                                                <input
                                                    type="text"
                                                    name="microWarehouse.contactPerson"
                                                    value={formData.microWarehouse.contactPerson}
                                                    onChange={handleChange}
                                                    className="form-control"
                                                    placeholder="Contact person name"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Contact Number</label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Phone size={16} />
                                                </span>
                                                <input
                                                    type="tel"
                                                    name="microWarehouse.contactNumber"
                                                    value={formData.microWarehouse.contactNumber}
                                                    onChange={handleChange}
                                                    className="form-control"
                                                    placeholder="Contact number"
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-bold">Warehouse Coordinates</label>
                                            <input
                                                type="text"
                                                value={warehouseCoords}
                                                onChange={handleWarehouseCoordsChange}
                                                className={`form-control ${errors.warehouseCoords ? 'is-invalid' : ''}`}
                                                placeholder="lng,lat (e.g., 77.5946,12.9716)"
                                                style={{ fontFamily: 'monospace' }}
                                            />
                                            {errors.warehouseCoords && <div className="invalid-feedback">{errors.warehouseCoords}</div>}
                                        </div>

                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-bold">Warehouse Address</label>
                                            <textarea
                                                name="microWarehouse.address"
                                                value={formData.microWarehouse.address}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="Enter complete warehouse address"
                                                rows="3"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary Panel */}
                        <div className="col-lg-4">
                            <div className="card shadow-sm sticky-top">
                                <div className="card-header bg-light">
                                    <h5 className="mb-0">Sector Summary</h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <strong>Sector Name:</strong>
                                        <div className="text-muted">
                                            {formData.name || <em>Not specified</em>}
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Serviceable Area:</strong>
                                        <div className="text-muted">
                                            {formData.serviceableArea ? (
                                                serviceableAreas?.find(area => area._id === formData.serviceableArea)?.name
                                            ) : (
                                                <em>Not selected</em>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Status:</strong>
                                        <div>
                                            <span className={`badge bg-${formData.isActive ? 'success' : 'secondary'}`}>
                                                {formData.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Max Drive Time:</strong>
                                        <div className="text-muted">
                                            {formData.maxDriveTimeMinutes} minutes
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <strong>Boundary Points:</strong>
                                        <div className="text-muted">
                                            {coordinates.trim() 
                                                ? `${coordinates.trim().split(' ').length} coordinates`
                                                : <em>No coordinates set</em>
                                            }
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <strong>Warehouse:</strong>
                                        <div className="text-muted">
                                            {formData.microWarehouse.address 
                                                ? formData.microWarehouse.contactPerson || 'Configured'
                                                : <em>Not configured</em>
                                            }
                                        </div>
                                    </div>

                                    {formData.description && (
                                        <div className="mb-3">
                                            <strong>Description:</strong>
                                            <div className="text-muted small">
                                                {formData.description}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex justify-content-end gap-2">
                                <Link
                                    to="/inventory/v1.2/sectors"
                                    className="btn btn-outline-secondary"
                                >
                                    Cancel
                                </Link>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            {isNewSector ? 'Creating...' : 'Updating...'}
                                        </>
                                    ) : (
                                        <>
                                            <Save size={16} className="me-2" />
                                            {isNewSector ? 'Create Sector' : 'Update Sector'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </Fragment>
    );
};

Sector.propTypes = {
    createSector: PropTypes.func.isRequired,
    getSector: PropTypes.func.isRequired,
    updateSector: PropTypes.func.isRequired,
    clearSector: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    sector: PropTypes.object,
    serviceableAreas: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    sector: state.sector.sector,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    loading: state.sector.loading
});

export default connect(mapStateToProps, {
    createSector,
    getSector,
    updateSector,
    clearSector,
    getServiceableAreas
})(Sector);
